import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import {
  DashboardIcon,
  UsersIcon,
  SecurityIcon,
  SettingsIcon,
  SunIcon,
  MoonIcon,
  MailIcon,
  KeyIcon,
  ServerIcon,
  AIIcon,
  FileTextIcon,
  DatabaseIcon,
  LayersIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldIcon,
  CpuIcon,
  TerminalIcon,
  ActivityIcon,
  UserIcon,
  BuildingIcon,
  CloseIcon,
  UploadCloudIcon,
  CloudIcon,
  ZapIcon
} from './common/Icons';
import { ChatIcon, ChatPanel } from './chat';
const NotificationCenter = React.lazy(() => import('./notifications/NotificationCenter'));
import UserMenu, { isSuperAdmin, isAdmin } from './common/UserMenu';
import { useTenantBranding } from './TenantBrandingProvider';
import { useTenantNavigationOptional } from './TenantNavigationProvider';
import { resolveView, type ViewContext } from './ViewRegistry';

import { useEffectiveSchema } from '../hooks/useEffectiveSchema';
import { buildNavGroups } from './TemplateNavRenderer';
import UserDashboard from './UserDashboard';
import { hasAreaDashboard, GROUP_TO_AREA_MAP } from '../features/tenants/config/functionalAreaConfigs';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  view?: string;  // view registry key (template-driven items)
  requiredFeature?: string;    // WQ-076
  requiredPlan?: string[];     // WQ-076
  requiredRoles?: string[];    // WQ-217 → WQ-223: item-level role gating (Phase 4.9 allocation-aware nav)
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  requiredRoles?: string[];    // role gating
  requiredFeature?: string;    // feature flag gating (WQ-076)
  requiredPlan?: string[];     // plan tier gating (WQ-076)
  items: NavItem[];
}

interface PortalProps {
  onLogout: () => void;
  userEmail?: string;
  userName?: string;
  userRole?: string;
}

/**
 * Unified navigation filter — checks role, features, AND plan tier (WQ-076).
 *
 * Visibility formula per group/item:
 *   visible = matchesRole AND matchesFeature AND matchesPlan
 *
 * Where:
 *   - No requiredRoles → visible to all roles
 *   - No requiredFeature → visible regardless of features
 *   - No requiredPlan → visible regardless of tier
 *   - Super admin bypasses ALL checks
 */
function filterNavGroups(
  groups: NavGroup[],
  role: string,
  tenantFeatures: string[],
  tenantTier: string | null,
): NavGroup[] {
  const normalizedRole = role.toLowerCase();
  const isSuperAdminUser = isSuperAdmin(role);

  return groups
    .filter(group => {
      // WQ-078: Super admin has no personal tenant_id, so tenant-portal
      // endpoints (Tenant Settings, Usage Dashboard) return 403.
      // Fixed in WQ-019, WQ-060, and again here after WQ-076 Portal rewrite.
      // DO NOT REMOVE this filter without giving super admins a tenant context.
      if (isSuperAdminUser) return group.id !== 'my-tenant';

      // Role check
      if (group.requiredRoles && group.requiredRoles.length > 0) {
        if (!group.requiredRoles.some(r => r.toLowerCase() === normalizedRole)) {
          return false;
        }
      }

      // Feature check (WQ-076)
      if (group.requiredFeature) {
        if (!tenantFeatures.includes(group.requiredFeature)) {
          return false;
        }
      }

      // Plan tier check (WQ-076)
      if (group.requiredPlan && group.requiredPlan.length > 0) {
        if (!tenantTier || !group.requiredPlan.includes(tenantTier)) {
          return false;
        }
      }

      return true;
    })
    .map(group => ({
      ...group,
      // Also filter items within visible groups
      items: group.items.filter(item => {
        if (isSuperAdminUser) return true;

        // Item-level role check (WQ-217 → WQ-223)
        if (item.requiredRoles && item.requiredRoles.length > 0) {
          if (!item.requiredRoles.some(r => r.toLowerCase() === normalizedRole)) {
            return false;
          }
        }

        if (item.requiredFeature) {
          if (!tenantFeatures.includes(item.requiredFeature)) {
            return false;
          }
        }

        if (item.requiredPlan && item.requiredPlan.length > 0) {
          if (!tenantTier || !item.requiredPlan.includes(tenantTier)) {
            return false;
          }
        }

        return true;
      }),
    }))
    .filter(group => group.items.length > 0);  // Remove empty groups
}

// Platform admin navigation — hardcoded fallback when no template is assigned
const PLATFORM_ADMIN_NAV: NavGroup[] = [
  {
    id: 'access',
    label: 'Access Control',
    icon: <ShieldIcon size={18} />,
    items: [
      { id: 'users', label: 'Users', icon: <UsersIcon size={18} /> },
      { id: 'roles', label: 'Roles', icon: <SecurityIcon size={18} /> },
      { id: 'requests', label: 'Account Requests', icon: <UserIcon size={18} /> },
      { id: 'sso', label: 'SSO & Integration', icon: <KeyIcon size={18} /> },
    ]
  },
  {
    id: 'ai',
    label: 'AI Command Center',
    icon: <CpuIcon size={18} />,
    items: [
      { id: 'ai-command-center', label: 'Command Center', icon: <AIIcon size={18} /> },
      { id: 'allocations', label: 'Allocations', icon: <ZapIcon size={18} /> },
      { id: 'skill-commissioning', label: 'Skill Commissioning', icon: <ShieldIcon size={18} /> },
      { id: 'rag-collections', label: 'RAG Collections', icon: <DatabaseIcon size={18} /> },
    ]
  },
  {
    id: 'security',
    label: 'Security',
    icon: <SecurityIcon size={18} />,
    items: [
      { id: 'security', label: 'Security Dashboard', icon: <SecurityIcon size={18} /> },
    ]
  },
  {
    id: 'bi',
    label: 'BI Command Center',
    icon: <ActivityIcon size={18} />,
    items: [
      { id: 'bi-command-center', label: 'Intelligence Center', icon: <ActivityIcon size={18} /> },
    ]
  },
  {
    id: 'platform',
    label: 'Platform',
    icon: <TerminalIcon size={18} />,
    items: [
      { id: 'api', label: 'API Management', icon: <ServerIcon size={18} /> },
      { id: 'schema', label: 'Schema', icon: <DatabaseIcon size={18} /> },
      { id: 'docs', label: 'Documentation', icon: <FileTextIcon size={18} /> },
      { id: 'templates', label: 'Templates', icon: <LayersIcon size={18} /> },
    ]
  },
  {
    id: 'tenant-mgmt',
    label: 'Tenant Management',
    icon: <BuildingIcon size={18} />,
    items: [
      { id: 'tenants', label: 'Tenants', icon: <BuildingIcon size={18} /> },
      { id: 'upgrades', label: 'Upgrades', icon: <UploadCloudIcon size={18} /> },
      { id: 'containers', label: 'Containers', icon: <ServerIcon size={18} /> },
    ]
  },
  {
    id: 'my-tenant',
    label: 'My Tenant',
    icon: <BuildingIcon size={18} />,
    items: [
      { id: 'tenant-settings', label: 'Tenant Settings', icon: <SettingsIcon size={18} /> },
      { id: 'tenant-usage', label: 'Usage Dashboard', icon: <ActivityIcon size={18} /> },
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: <ActivityIcon size={18} />,
    items: [
      { id: 'work-items', label: 'Work Items', icon: <FileTextIcon size={18} /> },
      { id: 'cloud-ops', label: 'Cloud Ops', icon: <CloudIcon size={18} /> },
      { id: 'usage', label: 'Usage Metrics', icon: <ActivityIcon size={18} /> },
      { id: 'platform-backups', label: 'Backups', icon: <DatabaseIcon size={18} /> },
      { id: 'logs', label: 'Logs', icon: <LayersIcon size={18} /> },
      { id: 'email', label: 'Email', icon: <MailIcon size={18} /> },
    ]
  },
];

// Error boundary to catch rendering crashes and show diagnostics instead of blank page
class ContentErrorBoundary extends React.Component<
  { activeTab: string; children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ContentErrorBoundary] Render crash:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: { activeTab: string }) {
    // Reset error when user navigates to a different tab
    if (prevProps.activeTab !== this.props.activeTab && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>
            Something went wrong
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontFamily: 'monospace', fontSize: '13px' }}>
            {this.state.error.message}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => this.setState({ error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const Portal: React.FC<PortalProps> = ({ onLogout, userEmail, userName, userRole = 'user' }) => {
  const { theme, toggleTheme } = useTheme();
  const { authFetch } = useAuth();
  const branding = useTenantBranding();
  const tenantNav = useTenantNavigationOptional();
  const { activeTenant, exitTenant, isLoading: isContextLoading, isInTenantContext, tenantContextSource } = useAdminContext();

  // Notification bell state
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // WQ-223 (was WQ-178, dead in AdminPortal): tenant AI kill-switch indicator
  const [tenantAIActive, setTenantAIActive] = useState(true);

  // Notification unread count polling — uses ref for stable authFetch reference
  const authFetchRef = React.useRef(authFetch);
  authFetchRef.current = authFetch;
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await authFetchRef.current('/api/admin/notifications/unread-count');
        if (!cancelled && res.ok) {
          const data = await res.json();
          setUnreadCount(data.count ?? 0);
        }
      } catch {
        // Silent — bell shows with 0 count
      }
    };
    const timeout = setTimeout(fetchCount, 2000);
    const interval = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearTimeout(timeout); clearInterval(interval); };
  }, []);

  // WQ-223 (was WQ-178, dead in AdminPortal): poll tenant AI kill-switch status
  // Only runs when entered into a tenant. Uses ref pattern to avoid authFetch dependency churn.
  useEffect(() => {
    if (!activeTenant?.id) {
      setTenantAIActive(true);
      return;
    }
    let cancelled = false;
    const checkAI = async () => {
      try {
        const res = await authFetchRef.current(`/api/admin/tenants/${activeTenant.id}/ai/kill-switch`);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setTenantAIActive(!data.active);
        }
      } catch {
        if (!cancelled) setTenantAIActive(true);
      }
    };
    checkAI();
    const interval = setInterval(checkAI, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeTenant?.id]);

  // Fetch effective schema for template-driven navigation (WQ-081)
  // Super admin browsing a specific tenant → admin endpoint (has WILDCARD permission)
  // Everyone else (tenant_admin, admin, user) → tenant-portal endpoint
  //   - Reads JWT tenant_id server-side (set by WQ-078)
  //   - Requires only TAB_DASHBOARD permission (tenant_admin has this)
  //   - Returns 400 if no tenant context → null schema → appropriate fallback
  const userIsSuperAdmin = isSuperAdmin(userRole);
  const { schema: effectiveSchema } = useEffectiveSchema(
    userIsSuperAdmin ? (activeTenant?.id || undefined) : undefined,
    userIsSuperAdmin
  );

  // WQ-085: Define platform-only views that must NEVER render in tenant context.
  // These views fetch platform-scoped data (all tenants, platform health, etc.)
  // WQ-086: 'dashboard' removed — DashboardPage is now context-aware and renders
  // the correct tier (platform admin, tenant admin, or tenant user landing).
  const PLATFORM_ONLY_VIEWS = new Set([
    'tenants', 'containers', 'upgrades',           // Tenant management
    'usage', 'platform-backups', 'logs', 'email', 'cloud-ops',  // Operations
    'api', 'schema', 'docs', 'templates',           // Platform admin
    'users', 'roles', 'requests',                    // Access control (platform-wide)
    'security',                                       // WQ-G security dashboard (multi-skill-aware)
  ]);

  // WQ-089: isInTenantContext now comes from AdminContext (checks both
  // explicit activeTenant AND implicit JWT tid claim for SSO users)

  // Determine navigation source and apply unified filtering
  const filteredNavGroups = useMemo(() => {
    const templateNavGroups = effectiveSchema?.navigation?.groups
      ? buildNavGroups(effectiveSchema.navigation.groups)
      : null;

    // WQ-085: When in tenant context, ONLY show template nav.
    // Never fall back to PLATFORM_ADMIN_NAV — that exposes platform views.
    // 1. Super admin in tenant context WITH template → template nav only
    // 2. Super admin in tenant context WITHOUT template → empty (show tenant welcome)
    // 3. Super admin without tenant context → platform admin nav
    // 4. User/tenant_admin with template → template nav
    // 5. User without template → null (show basic dashboard)
    let baseNavGroups: NavGroup[] | null;
    if (isInTenantContext) {
      // Tenant context: template nav only, no platform fallback
      baseNavGroups = templateNavGroups || null;
    } else {
      // Platform context: template nav or platform admin fallback
      baseNavGroups = templateNavGroups || (
        isSuperAdmin(userRole) ? PLATFORM_ADMIN_NAV : null
      );
    }

    if (!baseNavGroups) return [];

    // Get tenant features and tier from effective schema
    const tenantFeatures = effectiveSchema?.tenant_features || effectiveSchema?.features || [];
    const tenantTier = effectiveSchema?.tenant_tier || null;

    // Apply tenant config filtering first, then unified role/feature/plan filtering
    const tenantFilteredGroups = tenantNav.filterGroups(baseNavGroups);
    return filterNavGroups(tenantFilteredGroups, userRole, tenantFeatures, tenantTier);
  }, [effectiveSchema, tenantNav, userRole, activeTenant, isInTenantContext]);

  // WQ-246: activeTab is derived from URL path (React Router migration).
  // Path `/foo` → activeTab `foo`; `/` or `/dashboard` → `dashboard`.
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useMemo(() => {
    const seg = location.pathname.replace(/^\//, '').split('/')[0];
    return seg || 'dashboard';
  }, [location.pathname]);

  // WQ-246: localStorage `admin-initial-tab` back-compat (one-shot consume).
  // Pre-WQ-246 programmatic flows could set this to seed Portal's initial tab.
  // No active writers in src/ as of WQ-246, but the consumer stays so any
  // future programmatic flow keeps working without code changes here.
  useEffect(() => {
    const initialTab = localStorage.getItem('admin-initial-tab');
    if (initialTab) {
      localStorage.removeItem('admin-initial-tab');
      if (activeTab === 'dashboard') {
        navigate(`/${initialTab}`, { replace: true });
      }
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track which groups are expanded - load from localStorage
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('admin-nav-expanded');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set(['access', 'ai']); // Default expanded
      }
    }
    return new Set(['access', 'ai']); // Default expanded
  });

  // WQ-085 + WQ-089 (WQ-246): Reset activeTab via URL when entering/exiting tenant context.
  // Tracks the prior value so deep-link mounts at platform paths (e.g., /users)
  // are not redirected to /dashboard on first render. Only the actual context
  // transition triggers navigation. Uses replace:true so platform paths don't
  // accumulate in browser history when tenant context is active.
  const prevInTenantContext = React.useRef(isInTenantContext);
  useEffect(() => {
    if (isInTenantContext) {
      const firstItem = filteredNavGroups[0]?.items?.[0];
      if (firstItem && (activeTab === 'dashboard' || PLATFORM_ONLY_VIEWS.has(activeTab))) {
        navigate(`/${firstItem.id}`, { replace: true });
      }
    } else if (prevInTenantContext.current === true) {
      // Just exited tenant context: return to platform dashboard.
      navigate('/dashboard', { replace: true });
    }
    prevInTenantContext.current = isInTenantContext;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInTenantContext]);

  // WQ-089 (WQ-246): SSO users have isInTenantContext=true from mount but nav
  // groups load async. Once groups arrive, redirect from stale 'dashboard'
  // to first template nav item (replace: true to avoid history pollution).
  useEffect(() => {
    if (tenantContextSource === 'jwt' && filteredNavGroups.length > 0) {
      const firstItem = filteredNavGroups[0]?.items?.[0];
      if (firstItem && (activeTab === 'dashboard' || PLATFORM_ONLY_VIEWS.has(activeTab))) {
        navigate(`/${firstItem.id}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantContextSource, filteredNavGroups]);

  // WQ-246: admin-navigate custom-event back-compat. Step 5 migrated all
  // in-tree dispatchers to useNavigate(); listener stays for any external
  // dispatcher we missed or that's added later.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ tab: string }>).detail;
      if (detail?.tab) {
        navigate(`/${detail.tab}`);
      }
    };
    window.addEventListener('admin-navigate', handler);
    return () => window.removeEventListener('admin-navigate', handler);
  }, [navigate]);

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('admin-nav-expanded', JSON.stringify([...expandedGroups]));
  }, [expandedGroups]);

  // Auto-expand group when navigating to a child item
  useEffect(() => {
    for (const group of filteredNavGroups) {
      if (group.items.some(item => item.id === activeTab)) {
        setExpandedGroups(prev => {
          if (prev.has(group.id)) return prev; // already expanded — no state change
          return new Set([...prev, group.id]);
        });
        break;
      }
    }
  }, [activeTab, filteredNavGroups]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });

    // WQ-087a (WQ-246): Navigate to area dashboard when clicking a configured functional area group header.
    // The area key may differ from the group ID (e.g., 'hr' → 'people-culture').
    if (isInTenantContext && hasAreaDashboard(groupId)) {
      const areaKey = GROUP_TO_AREA_MAP[groupId] || groupId;
      navigate(`/${areaKey}`);
    }
  };

  const isGroupActive = (group: NavGroup): boolean => {
    // Check if any item in this group is active
    if (group.items.some(item => item.id === activeTab)) return true;
    // WQ-087a: Also check if the area dashboard for this group is active
    const areaKey = GROUP_TO_AREA_MAP[group.id];
    if (areaKey && activeTab === areaKey) return true;
    // Direct group ID match (when group ID equals area key, e.g., 'executive')
    if (activeTab === group.id && hasAreaDashboard(group.id)) return true;
    return false;
  };

  // Navigation handlers for UserMenu (WQ-246)
  const handleNavigateToProfile = useCallback(() => {
    navigate('/profile');
  }, [navigate]);

  const handleNavigateToSettings = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  const handleNavigateToTenants = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // Find the active nav item to get its view key and group context
  const findActiveNavItem = (tabId: string) => {
    for (const group of filteredNavGroups) {
      for (const item of group.items) {
        if (item.id === tabId) {
          return { ...item, groupId: group.id, groupLabel: group.label };
        }
      }
    }
    return null;
  };

  const renderContent = () => {
    // No-template fallback for non-admin users
    if (filteredNavGroups.length === 0 && !isSuperAdmin(userRole) && !isAdmin(userRole)) {
      return <UserDashboard userName={userName} />;
    }

    // WQ-085: Tenant context isolation guard — block platform-only views
    // When in tenant context, redirect platform views to exec-overview or first available view
    let viewKey = activeTab;
    if (isInTenantContext && PLATFORM_ONLY_VIEWS.has(viewKey)) {
      // Find the first real view from template nav, or fall back to exec-overview
      const firstItem = filteredNavGroups[0]?.items?.[0];
      viewKey = firstItem?.view || firstItem?.id || 'exec-overview';
    }

    // Access guard: redirect non-super-admins away from tenants
    if (viewKey === 'tenants' && !isSuperAdmin(userRole)) {
      viewKey = 'dashboard';
    }

    // Resolve view from registry (falls back to StubPage for unknown keys)
    const activeItem = findActiveNavItem(activeTab);
    const resolvedKey = activeItem?.view || viewKey;
    const ViewComponent = resolveView(resolvedKey);

    console.log('[Portal] renderContent:', { activeTab, resolvedKey, isInTenantContext, hasComponent: !!ViewComponent, componentName: ViewComponent?.displayName || ViewComponent?.name });

    const viewContext: ViewContext = {
      viewKey: resolvedKey,
      navItemId: activeTab,
      groupId: activeItem?.groupId,
      groupLabel: activeItem?.groupLabel,
      itemLabel: activeItem?.label,
      stubMetadata: effectiveSchema?.stub_metadata?.[activeTab] ||
                    effectiveSchema?.stub_metadata?.[resolvedKey],
    };

    return <ViewComponent context={viewContext} />;
  };

  // Dynamic sidebar subtitle
  const sidebarSubtitle = isSuperAdmin(userRole)
    ? (activeTenant ? activeTenant.name : 'Admin Portal')
    : isInTenantContext
      ? 'Portal'
      : 'Portal';

  return (
    <div className="admin-portal">
      {/* Chat Panel Overlay */}
      <ChatPanel />

      {/* Sidebar */}
      <aside className="admin-sidebar">
        {/* Logo */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            {branding.logo ? (
              <img
                src={branding.logo}
                alt={branding.appName}
                style={{ width: 28, height: 28, objectFit: 'contain' }}
              />
            ) : (
              <LayersIcon size={28} />
            )}
          </div>
          <div className="sidebar-brand">
            <span className="sidebar-title">{branding.appName}</span>
            <span className="sidebar-subtitle">{sidebarSubtitle}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {/* Dashboard - Always visible at top */}
          {/* WQ-085: In tenant context, hide platform dashboard button.
              Template nav groups provide the tenant-scoped navigation. */}
          {!isInTenantContext && (
            <Link
              to="/dashboard"
              className={`sidebar-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            >
              <span className="nav-icon"><DashboardIcon size={18} /></span>
              <span className="nav-label">Dashboard</span>
            </Link>
          )}

          {/* Grouped Navigation */}
          <div className="nav-groups">
            {filteredNavGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.id);
              const groupActive = isGroupActive(group);
              const groupLabel = tenantNav.getGroupLabel(group.id) || group.label;

              return (
                <div key={group.id} className="nav-group">
                  {/* Group Header */}
                  <button
                    className={`nav-group-header ${groupActive ? 'group-active' : ''}`}
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className="nav-group-icon">{group.icon}</span>
                    <span className="nav-group-label">{groupLabel}</span>
                    <span className={`nav-group-chevron ${isExpanded ? 'expanded' : ''}`}>
                      {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                    </span>
                  </button>

                  {/* Group Items */}
                  <div className={`nav-group-items ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    {group.items.map((item) => {
                      const itemLabel = tenantNav.getItemLabel(item.id) || item.label;
                      return (
                        <Link
                          key={item.id}
                          to={`/${item.id}`}
                          className={`sidebar-nav-item nav-child-item ${activeTab === item.id ? 'active' : ''}`}
                        >
                          <span className="nav-icon">{item.icon}</span>
                          <span className="nav-label">{itemLabel}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer Actions */}
        <div className="sidebar-footer">
          {activeTenant && userIsSuperAdmin && (
            <Link
              to="/features"
              className={`sidebar-action ${activeTab === 'features' ? 'active' : ''}`}
            >
              <LayersIcon size={18} />
              <span>Features</span>
            </Link>
          )}
          <Link
            to="/settings"
            className={`sidebar-action ${activeTab === 'settings' ? 'active' : ''}`}
          >
            <SettingsIcon size={18} />
            <span>Settings</span>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-content">
        {/* Top Header Bar */}
        <header className="admin-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(10px)',
          gap: '12px',
        }}>
          {/* Left side - Tenant Context Indicator */}
          {/* WQ-089: Three states:
              1. Explicit context (super admin entered tenant) → show tenant name + exit button
              2. JWT context (SSO user) → show tenant indicator, NO exit button
              3. No context → show "Platform Admin" or "Portal" */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isInTenantContext ? (
              <div
                className="tenant-context-indicator"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  background: 'rgba(124, 58, 237, 0.15)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <BuildingIcon size={16} style={{ color: 'var(--primary)' }} />
                <span style={{ color: 'var(--text-secondary)' }}>
                  {tenantContextSource === 'explicit' ? 'Viewing:' : 'Tenant:'}
                </span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  {activeTenant?.name || 'My Organization'}
                </span>
                {/* WQ-223 (was WQ-178, dead in AdminPortal): AI kill-switch status indicator.
                    Only shown for explicit context (super admin entered) where activeTenant.id exists. */}
                {tenantContextSource === 'explicit' && activeTenant?.id && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                    <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: tenantAIActive ? '#22c55e' : '#dc2626',
                    }} />
                    <span style={{ fontSize: 11, color: tenantAIActive ? '#22c55e' : '#dc2626' }}>
                      {tenantAIActive ? 'AI Active' : 'AI Halted'}
                    </span>
                  </span>
                )}
                {/* WQ-089: Only show exit button for explicit context (super admin).
                    SSO users have no platform view to return to. */}
                {tenantContextSource === 'explicit' && (
                  <button
                    onClick={exitTenant}
                    disabled={isContextLoading}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '2px',
                      cursor: isContextLoading ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--text-secondary)',
                      borderRadius: '4px',
                      opacity: isContextLoading ? 0.5 : 1,
                    }}
                    title="Exit tenant view"
                  >
                    <CloseIcon size={14} />
                  </button>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                }}
              >
                <BuildingIcon size={16} />
                <span>{isSuperAdmin(userRole) ? 'Platform Admin' : 'Portal'}</span>
              </div>
            )}
          </div>

          {/* Right side actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Bell Icon — notification bell with unread badge */}
            <button
              onClick={() => setNotificationPanelOpen(!notificationPanelOpen)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
              title="Notifications"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  background: '#dc2626',
                  color: '#fff',
                  borderRadius: '50%',
                  width: unreadCount > 9 ? '20px' : '16px',
                  height: '16px',
                  fontSize: '10px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <ChatIcon />
            <button
              className="header-action-btn"
              onClick={toggleTheme}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                transition: 'all 0.2s ease',
              }}
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <SunIcon size={20} /> : <MoonIcon size={20} />}
            </button>
            {/* User Menu */}
            <UserMenu
            user={{
              name: userName || 'User',
              email: userEmail || '',
              role: userRole,
            }}
            currentView="admin"
            onLogout={onLogout}
            onNavigateToProfile={handleNavigateToProfile}
            onNavigateToSettings={handleNavigateToSettings}
            onNavigateToTenants={handleNavigateToTenants}
          />
          </div>
        </header>

        {/* Page Content */}
        <div className="admin-page-content" style={{ flex: 1, overflow: 'auto' }}>
          <ContentErrorBoundary activeTab={activeTab}>
            {renderContent()}
          </ContentErrorBoundary>
        </div>
      </main>

      {/* Notification Center Overlay */}
      {notificationPanelOpen && (
        <React.Suspense fallback={null}>
          <NotificationCenter
            onClose={() => setNotificationPanelOpen(false)}
            onCountChange={setUnreadCount}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default Portal;

// Re-export for backward compatibility
export { filterNavGroups, PLATFORM_ADMIN_NAV };
export type { NavGroup, NavItem, PortalProps };
